import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Container, Row, Spinner } from 'react-bootstrap';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Shimmer } from 'react-shimmer';
import { useInView } from 'react-intersection-observer';
import api from '../services/api';
import { storeAdvertInteraction } from '../services/advertService';
import CommentForm from '../components/CommentForm';
import { InlineShareButtons } from 'sharethis-reactjs';
import { useAdverts } from '../hooks/useAdverts';
import AdvertPopup from '../components/AdvertPopup';
import GoogleAdsenseUnit from '../components/GoogleAdsenseUnit';

// Import images
import contactBg from '../assets/images/contact-bg.png';
import profileImg from '../assets/images/profile1.png';
import singleBlog from '../assets/images/single-blog.png';
import checkIcon from '../assets/images/check.png';
import userImg from '../assets/images/user-img.png';
import userImg2 from '../assets/images/us2.png';
import userImg3 from '../assets/images/us3.png';

//Import SVG Icons
import {SingleBlogBannerSVG} from '../components/SvgIcons';

// Custom function to update meta tags
const updateMetaTags = (metaTags) => {
  const head = document.head;

  const setMetaTag = (key, value, attribute = 'name') => {
    let meta = document.querySelector(`meta[${attribute}="${key}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attribute, key);
      head.appendChild(meta);
    }
    meta.setAttribute('content', value);
  };

  document.title = metaTags.title;
  setMetaTag('description', metaTags.description);
  setMetaTag('keywords', metaTags.keywords);
  setMetaTag('og:title', metaTags.title, 'property');
  setMetaTag('og:description', metaTags.description, 'property');
  setMetaTag('og:image', metaTags.ogImage, 'property');
  setMetaTag('og:url', window.location.href, 'property');
  setMetaTag('twitter:card', 'summary_large_image');
  setMetaTag('twitter:title', metaTags.title);
  setMetaTag('twitter:description', metaTags.description);
  setMetaTag('twitter:image', metaTags.twitterImage);
};

// Custom hook for fetching blog details with pagination
const useBlogDetail = (parent_slug, child_slug, blog_slug, page) => {
  const category = parent_slug ? `${parent_slug}/${child_slug}` : child_slug;
  return useQuery({
    queryKey: ['blogDetail', parent_slug, child_slug, blog_slug, page],
    queryFn: async () => {
      const response = await api.get('/blogs/get-blog-detail', {
        params: { category, slug: blog_slug, is_mobile: 0, page },
      });
      return response.data;
    },
    enabled: !!child_slug && !!blog_slug,
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    retry: 3,
  });
};

const SingleBlog = () => {
  useEffect(() => {
    AOS.init({ once: true });
  }, []);

  const { parent_slug, child_slug, blog_slug } = useParams();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [allComments, setAllComments] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [blogDetails, setBlogDetails] = useState(null);
  const [totalComments, setTotalComments] = useState(0);
  const { ref: loadMoreRef, inView } = useInView();

  // Fetch blog details and comments
  const { data: blogData, isLoading, isError, error } = useBlogDetail(parent_slug, child_slug, blog_slug, page);

  // Fetch adverts with specific position keys
  const { data: popupAdvertData } = useAdverts({ position_key: 'blog_popup', ad_format: 'popup', useWebContent: true });
  const { data: introAdvertData } = useAdverts({ position_key: 'blog_detail_intro', ad_format: 'regular', useWebContent: true });
  const { data: endingAdvertData } = useAdverts({ position_key: 'blog_detail_ending', ad_format: 'regular', useWebContent: true });

  const [showAdvert, setShowAdvert] = useState(false);
  const [shownOnce, setShownOnce] = useState(false);
  const introViewTracked = useRef(false);
  const endingViewTracked = useRef(false);

  // Extract advert data
  function getRandomAdvert(advertData) {
    const arr = advertData?.dataset?.data;
    if (Array.isArray(arr) && arr.length > 0) {
      const idx = Math.floor(Math.random() * arr.length);
      return arr[idx];
    }
    return undefined;
  }
  const popupAdvert = getRandomAdvert(popupAdvertData);
  const introAdvert = getRandomAdvert(introAdvertData);
  const endingAdvert = getRandomAdvert(endingAdvertData);

  // Track views for intro and ending adverts
  useEffect(() => {
    if (introAdvert && !introViewTracked.current) {
      storeAdvertInteraction({
        position: 'blog_detail_intro',
        adverts_id: introAdvert.id,
        interaction_type: 0, // View
        web_url: window.location.href,
        useWebContent: true,
      });
      introViewTracked.current = true;
    }
    if (endingAdvert && !endingViewTracked.current) {
      storeAdvertInteraction({
        position: 'blog_detail_ending',
        adverts_id: endingAdvert.id,
        interaction_type: 0, // View
        web_url: window.location.href,
        useWebContent: true,
      });
      endingViewTracked.current = true;
    }
  }, [introAdvert, endingAdvert]);

  // Advert popup display logic
  useEffect(() => {
    if (!isLoading && popupAdvertData?.dataset?.data?.length && !shownOnce) {
      setShowAdvert(true);
      setShownOnce(true);
    }
  }, [isLoading, popupAdvertData, shownOnce]);

  // Store blog details on first load and update comments
  useEffect(() => {
    if (blogData?.dataset?.data) {
      if (page === 1) {
        // Store blog details only on the first page
        setBlogDetails(blogData.dataset.data);
        setTotalComments(blogData.dataset.data.comments?.pagination?.totalRecords || 0);
      }
      // Update comments
      setAllComments((prev) => {
        const newComments = blogData.dataset.data.comments?.data?.map((comment, i) => ({
          id: comment.id,
          userImage: [userImg, userImg2, userImg3][(i + prev.length) % 3],
          username: comment.username || 'Anonymous',
          timeAgo: comment.timeAgo || 'Unknown time',
          text: comment.text || 'No comment text',
        })) || [];
        if (page === 1) {
          return newComments;
        }
        return [...prev, ...newComments];
      });
      setHasMore(page < blogData.dataset.data.comments?.pagination?.totalPages);
    }
  }, [blogData, page]);

  // Load more comments when the load more ref is in view
  useEffect(() => {
    if (inView && hasMore && !isLoading) {
      setPage((prev) => prev + 1);
    }
  }, [inView, hasMore, isLoading]);

  // Optimistic update for new comment
  const handleCommentSuccess = (newComment) => {
    queryClient.setQueryData(['blogDetail', parent_slug, child_slug, blog_slug, page], (oldData) => {
      if (!oldData?.dataset?.data) return oldData;
      const newComments = [
        {
          id: Date.now(), // Temporary ID
          username: newComment.name,
          email: newComment.email,
          state_id: newComment.state_id,
          phone: newComment.phone,
          text: newComment.comment,
          created_at: new Date().toISOString(),
          state_name: newComment.state_name || '',
          timeAgo: 'Just now',
          userImage: userImg,
        },
        ...(oldData.dataset.data.comments?.data || []),
      ];
      return {
        ...oldData,
        dataset: {
          ...oldData.dataset,
          data: {
            ...oldData.dataset.data,
            comments: {
              ...oldData.dataset.data.comments,
              data: newComments,
              pagination: {
                ...oldData.dataset.data.comments.pagination,
                totalRecords: (oldData.dataset.data.comments.pagination.totalRecords || 0) + 1,
              },
            },
          },
        },
      };
    });
    // Refetch to sync with server
    queryClient.invalidateQueries(['blogDetail', parent_slug, child_slug, blog_slug, 1]);
    setPage(1); // Reset to first page to show new comment
    setAllComments([]); // Clear comments to refetch
    setTotalComments((prev) => prev + 1); // Update total comments count
  };

  // Update meta tags
  useEffect(() => {
    if (blogDetails) {
      const metaTags = {
        title: blogDetails.title || 'Blog Post',
        description:
          blogDetails.meta_description ||
          (blogDetails.short_description?.substring(0, 160).replace(/\\r\\n/g, ' ')) ||
          'Read the latest insights and updates on our blog.',
        keywords:
          (Array.isArray(blogDetails.keywords)
            ? blogDetails.keywords.join(', ')
            : blogDetails.keywords ||
              blogDetails.tags?.map((tag) => tag.name).join(', ') ||
              'blog, news, insights'),
        ogImage:
          blogDetails.social_image
            ? `${blogDetails.social_image}`
            : blogDetails.image_url
            ? `${blogDetails.image_url}`
            : `${window.location.origin}${singleBlog}`,
        twitterImage:
          blogDetails.social_image
            ? `${blogDetails.social_image}`
            : blogDetails.image_url
            ? `${blogDetails.image_url}`
            : `${window.location.origin}${singleBlog}`,
      };
      updateMetaTags(metaTags);
    }
  }, [blogDetails]);

  const blogMapped = blogDetails
    ? {
        blogId: blogDetails.blog_id,
        title: blogDetails.title || 'Untitled Blog',
        image: blogDetails.image_url
          ? `${blogDetails.image_url}`
          : blogDetails.blog_images?.[0] || singleBlog,
        category:
          (Array.isArray(blogDetails.categories)
            ? blogDetails.categories[0]?.name || 'General'
            : blogDetails.categories?.split(', ')[0] || 'General'),
        author: blogDetails.author || 'Anonymous',
        authorImg: blogDetails.author_img || profileImg,
        created_at: blogDetails.created_at || 'Unknown Date',
        total_views: blogDetails.total_views || 0,
        average_rating: blogDetails.average_rating || 0,
        short_description: blogDetails.short_description || '',
        description: blogDetails.description || '<p>No content available.</p>',
        tags: blogDetails.tags?.map((tag) => tag.name) || [],
        comments: allComments,
        states: blogDetails['contact-us-states'] || [],
      }
    : null;

  const handleCloseAdvert = () => setShowAdvert(false);

  const handleAdvertClick = (advert, position) => {
    if (advert.advert_url) {
      storeAdvertInteraction({
        position,
        adverts_id: advert.id,
        interaction_type: 1, // Click
        web_url: window.location.href,
        useWebContent: true,
      });
      window.open(advert.advert_url, '_blank', 'noopener,noreferrer');
    }
  };

  if (isLoading && page === 1) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
        }}
      >
        <img
          src="https://stg-admin.gkcloud.in/storage/settings/backend_favicon_1739509041.png"
          alt="Loading..."
          style={{
            width: '100px',
            height: 'auto',
            animation: 'pulse 1.5s infinite',
          }}
        />
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.1); }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </div>
    );
  }

  const category = parent_slug ? `${parent_slug}/${child_slug}` : child_slug;

  return (
    <>
     <section
        className="contact-banner singleblog-banner">
         <div className="svg-position svg-img singleblogSVG">
     <SingleBlogBannerSVG />
<div className="svg-content w-100">
        <div className="floating-dots">
          {[...Array(10)].map((_, i) => (
            <div className="dot" key={i}></div>
          ))}
        </div>
        <Container>
          <Row className="align-items-center singleblog-content">
            {isError || !blogMapped || !child_slug || !blog_slug ? (
              <div className="col-12 text-center">
                <p>Error: {error?.message || 'Invalid blog category or slug'}</p>
              </div>
            ) : (
              <>
                <div className="col-lg-12">
                  <Link to="/blog" className="blog-list">
                    <i className="fa-solid fa-arrow-left fa-fw"></i> Blog List
                  </Link>
                  <div className="blog-information">
                    <h6 className="mb-0">{blogMapped.category}</h6>
                    <ul>
                      <li>
                        <span>
                          {new Date(blogMapped.created_at).toLocaleDateString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      </li>
                      <li>
                        <span>{totalComments}</span>
                        <span> Comments</span>
                      </li>
                      <li>
                        <span>{blogMapped.total_views}</span>
                        <span> Views</span>
                      </li>
                    </ul>
                  </div>
                  <div className="section_title top_content">
                    <h2>{blogMapped.title}</h2>
                  </div>
                </div>
                <div className="blog-profile">
                  <img src={blogMapped.authorImg} alt={blogMapped.author} />
                  <div className="profile-text">
                    <h3>{blogMapped.author}</h3>
                    <p>Content Contributor</p>
                  </div>
                </div>
              </>
            )}
          </Row>
        </Container>
          </div>
        </div>
      </section>

      <div className="single-blog-img editor-choice">
        <Container>
          <Row>
            {isError || !blogMapped || !child_slug || !blog_slug ? (
              <div className="col-12 text-center">
                <p>Error: {error?.message || 'Invalid blog category or slug'}</p>
              </div>
            ) : (
              <>
                <div className="col-12 image-blog">
                  <img src={blogMapped.image} alt={blogMapped.title} />
                </div>
                <p className="mb-0">{blogMapped.short_description.replace(/\\r\\n/g, ' ')}</p>

                {/* Intro Advert */}
                {introAdvert && (
                  <div className="col-12 my-4">
                    <div
                      className="advert-card"
                      style={{
                        background: '#6a49f2',
                        borderRadius: '15px',
                        padding: '25px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '15px',
                          right: '15px',
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        Sponsored
                      </div>
                      <div className="row align-items-center">
                        {introAdvert.image_url && (
                          <div className="col-md-4 mb-3 mb-md-0">
                            <div
                              style={{
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                              }}
                            >
                              <img
                                src={introAdvert.image_url}
                                alt={introAdvert.name || 'Advertisement'}
                                className="w-100"
                                style={{
                                  height: '180px',
                                  objectFit: 'cover',
                                  transition: 'transform 0.3s ease',
                                }}
                                onMouseOver={(e) => (e.target.style.transform = 'scale(1.05)')}
                                onMouseOut={(e) => (e.target.style.transform = 'scale(1)')}
                              />
                            </div>
                          </div>
                        )}
                        <div className={introAdvert.image_url ? 'col-md-8' : 'col-12'}>
                          {introAdvert.name && (
                            <h4
                              style={{
                                color: 'white',
                                marginBottom: '12px',
                                fontWeight: '700',
                                fontSize: '1.5rem',
                              }}
                            >
                              {introAdvert.name}
                            </h4>
                          )}
                          {introAdvert.description && (
                            <p
                              style={{
                                color: 'rgba(255,255,255,0.9)',
                                marginBottom: '20px',
                                fontSize: '1rem',
                                lineHeight: '1.6',
                              }}
                            >
                              {introAdvert.description}
                            </p>
                          )}
                          {introAdvert.advert_url && (
                            <a
                              href={introAdvert.advert_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-block',
                                background: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                padding: '12px 25px',
                                borderRadius: '25px',
                                textDecoration: 'none',
                                fontWeight: '600',
                                border: '2px solid rgba(255,255,255,0.3)',
                                transition: 'all 0.3s ease',
                                backdropFilter: 'blur(10px)',
                              }}
                              onClick={() => handleAdvertClick(introAdvert, 'blog_detail_intro')}
                              onMouseOver={(e) => {
                                e.target.style.background = 'rgba(255,255,255,0.3)';
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 5px 15px rgba(0,0,0,0.2)';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.background = 'rgba(255,255,255,0.2)';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                              }}
                            >
                              Learn More →
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-12">
                  <div className="border-text">
                    <p className="mb-0">{blogMapped.short_description.split('. ')[0]}</p>
                  </div>
                </div>
                <div className="col-12 my-4">
                  <GoogleAdsenseUnit minHeight={140} />
                </div>
                <div
                  className="mb-4"
                  dangerouslySetInnerHTML={{ __html: blogMapped.description }}
                />
                <p className="mb-0">{blogMapped.short_description.replace(/\\r\\n/g, ' ')}</p>
                <ul className="Paragraph-list">
                  {blogMapped.tags.length > 0 ? (
                    blogMapped.tags.map((tag, i) => (
                      <li key={i}>
                        <img src={checkIcon} alt="" /> {tag}
                      </li>
                    ))
                  ) : (
                    <li>
                      <img src={checkIcon} alt="" /> No tags available
                    </li>
                  )}
                </ul>
                <p className="mb-0">{blogMapped.short_description.replace(/\\r\\n/g, ' ')}</p>
                <div className="col-12 my-4">
                  <InlineShareButtons
                    config={{
                      alignment: 'left',
                      color: 'social',
                      enabled: true,
                      font_size: 16,
                      language: 'en',
                      networks: ['facebook', 'whatsapp', 'twitter', 'pinterest', 'linkedin'],
                      radius: 50,
                      show_total: true,
                      size: 40,
                      url: window.location.href,
                      title: blogMapped.title || 'Check out this blog post!',
                      description: blogMapped.short_description?.replace(/\\r\\n/g, ' ') || 'Read more on our blog.',
                      image: blogMapped.image || 'https://stg-admin.gkcloud.in/storage/web-content/main_logo_1750141872.png',
                    }}
                  />
                </div>
                <div className="col-12 my-4">
                  <GoogleAdsenseUnit minHeight={140} />
                </div>

                {/* Ending Advert */}
                {endingAdvert && (
                  <div className="col-12 my-4">
                    <div
                      className="advert-card"
                      style={{
                        background: '#32236f',
                        borderRadius: '15px',
                        padding: '25px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '15px',
                          right: '15px',
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        Sponsored
                      </div>
                      <div className="row align-items-center">
                        {endingAdvert.image_url && (
                          <div className="col-md-4 mb-3 mb-md-0">
                            <div
                              style={{
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                              }}
                            >
                              <img
                                src={endingAdvert.image_url}
                                alt={endingAdvert.name || 'Advertisement'}
                                className="w-100"
                                style={{
                                  height: '180px',
                                  objectFit: 'cover',
                                  transition: 'transform 0.3s ease',
                                }}
                                onMouseOver={(e) => (e.target.style.transform = 'scale(1.05)')}
                                onMouseOut={(e) => (e.target.style.transform = 'scale(1)')}
                              />
                            </div>
                          </div>
                        )}
                        <div className={endingAdvert.image_url ? 'col-md-8' : 'col-12'}>
                          {endingAdvert.name && (
                            <h4
                              style={{
                                color: 'white',
                                marginBottom: '12px',
                                fontWeight: '700',
                                fontSize: '1.5rem',
                              }}
                            >
                              {endingAdvert.name}
                            </h4>
                          )}
                          {endingAdvert.description && (
                            <p
                              style={{
                                color: 'rgba(255,255,255,0.9)',
                                marginBottom: '20px',
                                fontSize: '1rem',
                                lineHeight: '1.6',
                              }}
                            >
                              {endingAdvert.description}
                            </p>
                          )}
                          {endingAdvert.advert_url && (
                            <a
                              href={endingAdvert.advert_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-block',
                                background: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                padding: '12px 25px',
                                borderRadius: '25px',
                                textDecoration: 'none',
                                fontWeight: '600',
                                border: '2px solid rgba(255,255,255,0.3)',
                                transition: 'all 0.3s ease',
                                backdropFilter: 'blur(10px)',
                              }}
                              onClick={() => handleAdvertClick(endingAdvert, 'blog_detail_ending')}
                              onMouseOver={(e) => {
                                e.target.style.background = 'rgba(255,255,255,0.3)';
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 5px 15px rgba(0,0,0,0.2)';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.background = 'rgba(255,255,255,0.2)';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = 'none';
                              }}
                            >
                              Learn More →
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="comment-heading comment-number">
                  <h3 className="blog-font mb-0">
                    {totalComments} Comment{totalComments !== 1 ? 's' : ''}
                  </h3>
                </div>
                <div className="all-comment">
                  {blogMapped.comments.map((comment, i) => (
                    <div className={`comment top-border ${i % 2 === 0 ? '' : 'even-comment'}`} key={comment.id}>
                      <div className="comment-description">
                        <div className="user-profile">
                          <img src={comment.userImage} alt={comment.username} />
                        </div>
                        <div className="comment-time">
                          <h6>{comment.timeAgo}</h6>
                          <h3>{comment.username}</h3>
                        </div>
                      </div>
                      <div className="comment-text">
                        <p className="mb-0">{comment.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {hasMore && (
                  <div ref={loadMoreRef} style={{ height: '20px', textAlign: 'center', margin: '20px 0' }}>
                    {isLoading && (
                      <Spinner animation="border" variant="primary" role="status">
                        <span className="visually-hidden">Loading more comments...</span>
                      </Spinner>
                    )}
                  </div>
                )}
                <CommentForm
                  blogId={blogMapped.blogId}
                  category={category}
                  slug={blog_slug}
                  states={blogMapped.states}
                  onCommentSuccess={handleCommentSuccess}
                />
              </>
            )}
          </Row>
        </Container>
      </div>

      <AdvertPopup
        advert={popupAdvert}
        show={showAdvert}
        onClose={handleCloseAdvert}
        position_key="blog_popup"
        useWebContent={true}
      />
    </>
  );
};

export default SingleBlog;

